import React, { useState, useEffect, useCallback } from 'react';
import { FiTag, FiPlus, FiEdit2, FiTrash2, FiX, FiCheck, FiLock } from 'react-icons/fi';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { canStaffAccess } from '../utils/staffPages';
import toast from 'react-hot-toast';

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 };
const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '8px 10px',
  border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none',
};

const EMPTY = { name: '', description: '' };

export default function AdminVendorTypes() {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || canStaffAccess(user?.allowedPages, 'vendor-types', 'edit');
  const [types, setTypes]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [form, setForm]         = useState(EMPTY);
  const [editId, setEditId]     = useState(null);
  const [showForm, setShowForm] = useState(false);

  const fetchTypes = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await API.get('/vendors/types');
      setTypes(data.types || []);
    } catch {
      toast.error('Failed to load vendor types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTypes(); }, [fetchTypes]);

  const openAdd  = () => { setForm(EMPTY); setEditId(null); setShowForm(true); };
  const openEdit = (t) => {
    if (t.is_system) return toast.error(`"${t.name}" is a system type. You can't rename it.`);
    setForm({ name: t.name, description: t.description || '' });
    setEditId(t.id);
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditId(null); setForm(EMPTY); };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    try {
      setSaving(true);
      if (editId) {
        await API.put(`/vendors/types/${editId}`, form);
        toast.success('Type updated');
      } else {
        await API.post('/vendors/types', form);
        toast.success('Type added');
      }
      closeForm();
      fetchTypes();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (t) => {
    if (t.is_system) return toast.error(`"${t.name}" is a system type and cannot be deactivated.`);
    try {
      await API.put(`/vendors/types/${t.id}`, { status: t.status === 'active' ? 'inactive' : 'active' });
      toast.success('Status updated');
      fetchTypes();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (t) => {
    if (t.is_system) return toast.error(`"${t.name}" is a system type and cannot be deleted.`);
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try {
      await API.delete(`/vendors/types/${t.id}`);
      toast.success('Deleted');
      fetchTypes();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  return (
    <div style={{ padding: '32px 24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontWeight: 700, fontSize: '1.4rem', margin: 0 }}>Vendor Types</h2>
          
        </div>
        <button className="crm-btn crm-btn--primary" onClick={openAdd} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {canEdit && <><FiPlus size={15} /> Add Type</>}
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="crm-card" style={{ marginBottom: 24, border: '1.5px solid #6366f1' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <span style={{ fontWeight: 600, fontSize: 15 }}>{editId ? 'Edit Type' : 'New Type'}</span>
            <button onClick={closeForm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}><FiX size={18} /></button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Name *</label>
              <input style={inputStyle} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Raw Material Supplier" />
            </div>
            <div>
              <label style={labelStyle}>Description</label>
              <input style={inputStyle} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional" />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="crm-btn" onClick={closeForm}>Cancel</button>
            <button className="crm-btn crm-btn--primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : editId ? 'Update' : 'Add Type'}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="crm-card">
        {loading ? (
          <p style={{ color: '#9ca3af', fontSize: 13 }}>Loading…</p>
        ) : types.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px 0' }}>
            <FiTag size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
            <p>No vendor types yet. Add one above.</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #f3f4f6' }}>
                {['Name', 'Description', 'Status', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 600, fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {types.map(t => (
                <tr key={t.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '12px 10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <span style={{ fontWeight: 600 }}>{t.name}</span>
                      {t.is_system ? (
                        <span title="System type — cannot be edited or deleted" style={{ display: 'flex', alignItems: 'center', color: '#9ca3af' }}>
                          <FiLock size={12} />
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td style={{ padding: '12px 10px', color: '#6b7280' }}>{t.description || '—'}</td>
                  <td style={{ padding: '12px 10px' }}>
                    <span style={{
                      padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600,
                      background: t.status === 'active' ? '#dcfce7' : '#f3f4f6',
                      color:      t.status === 'active' ? '#16a34a' : '#6b7280',
                    }}>{t.status}</span>
                  </td>
                  <td style={{ padding: '12px 10px' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => canEdit && openEdit(t)}
                        disabled={!canEdit || t.is_system}
                        title={!canEdit ? 'View only' : t.is_system ? 'System type — locked' : 'Edit'}
                        style={{ background: t.is_system ? '#f9fafb' : '#f3f4f6', border: 'none', borderRadius: 6, padding: '5px 8px', cursor: (!canEdit || t.is_system) ? 'not-allowed' : 'pointer', opacity: (!canEdit || t.is_system) ? 0.4 : 1 }}
                      >
                        <FiEdit2 size={14} />
                      </button>
                      <button
                        onClick={() => handleToggleStatus(t)}
                        title={t.is_system ? 'System type — locked' : (t.status === 'active' ? 'Deactivate' : 'Activate')}
                        disabled={t.is_system}
                        style={{ background: t.is_system ? '#f9fafb' : '#f3f4f6', border: 'none', borderRadius: 6, padding: '5px 8px', cursor: t.is_system ? 'not-allowed' : 'pointer', opacity: t.is_system ? 0.4 : 1 }}
                      >
                        {t.status === 'active'
                          ? <FiX size={14} color={t.is_system ? '#9ca3af' : '#dc2626'} />
                          : <FiCheck size={14} color={t.is_system ? '#9ca3af' : '#16a34a'} />}
                      </button>
                      <button
                        onClick={() => canEdit && handleDelete(t)}
                        disabled={!canEdit || t.is_system}
                        title={!canEdit ? 'View only' : t.is_system ? 'System type — locked' : 'Delete'}
                        style={{ background: t.is_system ? '#f9fafb' : '#fee2e2', border: 'none', borderRadius: 6, padding: '5px 8px', cursor: t.is_system ? 'not-allowed' : 'pointer', opacity: t.is_system ? 0.4 : 1 }}
                      >
                        <FiTrash2 size={14} color={t.is_system ? '#9ca3af' : '#dc2626'} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}